import React from 'react';
import { Menu, Search, Share2, X } from 'lucide-react';
import StatusIndicator from './StatusIndicator';
import HeaderUploadIndicator from './HeaderUploadIndicator';
import { useLanguage } from '../i18n';

/**
 * Main Office header. Panel toggles, settings, and the user menu now live
 * in the icon rail (WorkspaceShell), so this header focuses on the active
 * conversation: title, in-chat search and share. The notification bell left
 * this header in #437 — notifications are the Notifications column of the
 * "Reminders & Notifications" feature, signalled by a red dot on its sidebar
 * entry (and, on a phone, on the menu button below).
 */
const Header = ({
    apiStatus,
    onToggleSidebar,
    onSearchClick,
    onShareClick,
    showSidebar = true,
    currentConversationTitle,
    hasCurrentConversation = false,
    isReadOnly = false,
    // On mobile the top-left corner button opens the app-like overlay menu
    // instead of toggling the (hidden) sidebar — the parent swaps the handler
    // and these labels (Mobile Visual #171). On DESKTOP there is no such button
    // any more: the conversation column now carries its own arrow (#360).
    menuTestId = 'sidebar-toggle',
    menuTitle,
    showMenuButton = true,
    // #361 — the Main Chat is a closable occupant, so it needs an X like every
    // other one. Desktop only: a phone shows one view at a time and reaches the
    // others through the overlay menu.
    onCloseChat,
    // #437: an unseen notification — on a phone the overlay menu is the only way
    // to Reminders & Notifications, so its button carries the red dot.
    menuHasUnseen = false,
}) => {
    const { t } = useLanguage();
    return (
        <div className="border-b border-pai-hairline px-4 shell:px-[26px] pt-[22px] pb-[18px] flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    {showMenuButton && (
                        <button
                            onClick={onToggleSidebar}
                            data-testid={menuTestId}
                            className="relative pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                            title={menuTitle || (showSidebar ? t('navHideSidebar', 'Hide sidebar') : t('navShowSidebar', 'Show sidebar'))}
                        >
                            <Menu size={20} />
                            {menuHasUnseen && (
                                <span
                                    data-testid="mobile-menu-unseen-dot"
                                    aria-hidden="true"
                                    className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-pai-red"
                                />
                            )}
                        </button>
                    )}
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                            <h1 className="text-[27px] font-extrabold tracking-[-0.025em] text-white truncate">
                                {currentConversationTitle || 'PAI'}
                            </h1>
                            <StatusIndicator status={apiStatus} />
                        </div>
                        {isReadOnly && (
                            <span className="text-xs border border-pai-blue/50 text-pai-blue px-2.5 py-1 rounded-full font-semibold flex-shrink-0">
                                {t('navSharedReadOnly', 'Shared (read-only)')}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Upload progress indicator — only visible while uploads
                        are in flight or pending acknowledgement. Lives next
                        to the search button per the spec, not as an overlay
                        on the input. */}
                    <HeaderUploadIndicator />

                    {hasCurrentConversation && onSearchClick && (
                        <button
                            onClick={onSearchClick}
                            className="pai-icon-btn w-[38px] h-[38px]"
                            title={t('navSearchInConversation', 'Search in conversation')}
                        >
                            <Search size={20} />
                        </button>
                    )}
                    {hasCurrentConversation && !isReadOnly && onShareClick && (
                        <button
                            onClick={onShareClick}
                            className="pai-icon-btn w-[38px] h-[38px]"
                            title={t('navShareConversation', 'Share conversation')}
                        >
                            <Share2 size={20} />
                        </button>
                    )}

                    {/* Conversation deletion lives exclusively in the sidebar's
                        three-dots menu (Bin removal ticket), and the bell moved to
                        "Reminders & Notifications" (#437) — search and share are the
                        only conversation icons left here. */}
                    {onCloseChat && (
                        <button
                            onClick={onCloseChat}
                            data-testid="chat-close"
                            className="pai-icon-btn w-[38px] h-[38px]"
                            title={t('navCloseMainChat', 'Close Main Chat')}
                            aria-label={t('navCloseMainChat', 'Close Main Chat')}
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Header;
